import React from "react";
import styled from "styled-components";
import CaterpillarLogo from "../pngs/Caterpillar-Logo.png";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import MetricsCard from "../components/sub-components/MetricsCard";
import NDADisclosure from "../components/NDADisclosure";
import { ContrastPair } from "../components/diagrams/primitives";
import {
    EntityModel,
    MaintenanceFlow,
    ConnectedWorkflow,
    ReleaseLoop,
    RoleDomains,
} from "../components/CatDiagrams";

/**
 * Caterpillar.
 *
 * The earliest role on the site, and the one where the design/engineering line
 * started dissolving — so the page is built around a single reframe: the manual
 * stopped being the product, and the equipment became the centre of it.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Logo = styled.img`
    display: block;
    max-width: 200px;
    height: auto;
    /* The CAT mark is black artwork, and the theme's phosphor filter works by
       brightening — which cannot lift black off a dark screen. Invert first, then
       burn it into the phosphor like every other logo. */
    filter: invert(1) ${({ theme }) => theme.logoFilter};
    margin-bottom: ${spacing[700]};

    ${media.md} {
        max-width: 150px;
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
    columns: 2;
    column-gap: ${spacing[700]};

    ${media.md} {
        columns: 1;
    }
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[500]};
    position: relative;
    margin-bottom: ${spacing[200]};
    break-inside: avoid;

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const Shipped = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[500]};
    margin: ${spacing[700]} 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const ShippedItem = styled.div`
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-left: 2px solid ${({ color }) => color};
    padding: ${spacing[500]};
    min-width: 0;
`;

const ShippedName = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[300]};
`;

const ShippedDesc = styled.p`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

const SHIPPED = [
    {
        name: "Interactive Maintenance",
        color: colors.primary[500],
        desc: "Static maintenance documentation converted into structured, step-by-step mobile workflows usable in the field.",
    },
    {
        name: "Parts Discovery",
        color: colors.cyan[300],
        desc: "Searchable, filterable interfaces connecting equipment and maintenance needs to Caterpillar's parts ecosystem.",
    },
    {
        name: "Dealer & Service",
        color: colors.emerald[500],
        desc: "Flows carrying a customer from identifying a need to finding nearby locations and service options.",
    },
    {
        name: "Reusable Patterns",
        color: colors.amber[400],
        desc: "Production React and JavaScript components supporting many equipment, maintenance, parts, and service workflows.",
    },
];

const Caterpillar = () => (
    <PageWrapper>
        <Header>
            <Logo src={CaterpillarLogo} alt="Caterpillar logo" />
            <PageHeadline>Caterpillar</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Software Engineer &mdash; Frontend &amp; Mobile</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>Oct 2015 &mdash; Apr 2018</span>
                </MetaItem>
            </Meta>
            <Lede>
                Caterpillar ran on printed maintenance manuals, fragmented parts catalogs, and legacy
                support systems. I owned CAT Mobile from product definition through frontend
                implementation, in React and JavaScript, leading a five-person team and coordinating
                with international engineering. The challenge was never putting manuals on a screen
                &mdash; it was working out{" "}
                <strong>how decades of equipment information should behave as software</strong>.
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="Oct 2015 – Apr 2018"
                    roles={["Software Engineer", "Frontend", "Mobile"]}
                    tools={["React", "JavaScript", "Mobile", "Frontend Architecture"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        A printed manual has a very simple architecture. Pages sit in a fixed order,
                        the reader finds the right section, and follows it. Software does not work
                        that way &mdash; users needed to move between equipment, maintenance,
                        procedure, part, dealer, and service, in whatever order the job demanded.
                    </Para>
                    <Pull>
                        How do we transform a document-based support system into an application
                        architecture?
                    </Pull>
                    <Para>
                        That meant making a large body of technical information searchable,
                        navigable, and usable from a phone, while still serving customers who knew
                        the existing documentation well.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>From Documents to Structured Data</SectionHeadline>
                    <Para>
                        The first job was breaking the information out of the structure the printed
                        manual imposed on it.
                    </Para>
                    <ContrastPair
                        left={{
                            label: "Document model",
                            color: colors.coral[400],
                            text: "Book → Chapter → Page",
                        }}
                        right={{
                            label: "Application model",
                            color: colors.emerald[500],
                            text: "Equipment → Systems → Procedures → Steps → Parts → Service",
                        }}
                    />
                    <DiagramSlot>
                        <EntityModel />
                    </DiagramSlot>
                    <Para>
                        A maintenance procedure could now become an interactive workflow. A part
                        referenced mid-procedure could link into the catalog. A customer who needed
                        help could move from that workflow into dealer and service information.
                    </Para>
                    <Pull>
                        The manual stopped being the product. The equipment became the centre of it.
                    </Pull>
                </Section>

                <Section>
                    <SectionHeadline>Building CAT Mobile</SectionHeadline>
                    <Para>
                        I worked directly in React and JavaScript on production interfaces. Rather
                        than treating every feature as its own screen, we built reusable application
                        patterns around the behaviors that recurred:
                    </Para>
                    <Bullets>
                        <Bullet>Equipment information</Bullet>
                        <Bullet>Navigation and hierarchy</Bullet>
                        <Bullet>Maintenance procedures</Bullet>
                        <Bullet>Step-by-step instructions</Bullet>
                        <Bullet>Search and filtering</Bullet>
                        <Bullet>Parts discovery</Bullet>
                        <Bullet>Dealer information</Bullet>
                        <Bullet>Service actions</Bullet>
                    </Bullets>
                    <Para>
                        The goal was patterns that could carry a very large amount of equipment and
                        service information without a bespoke interface per scenario.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>The Maintenance Experience</SectionHeadline>
                    <Para>
                        A printed procedure might hold dozens of instructions, references, warnings,
                        diagrams, and related parts. Shrinking that document onto a phone would have
                        produced something unusable. So procedures became interactive sequences.
                    </Para>
                    <DiagramSlot>
                        <MaintenanceFlow />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Parts Discovery</SectionHeadline>
                    <Para>
                        Parts lived across a very large catalog, but customers were rarely browsing.
                        They were looking for something attached to a specific machine or a specific
                        maintenance task, so we built the interfaces around those relationships.
                    </Para>
                    <DiagramSlot>
                        <ConnectedWorkflow />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Reusable Frontend Architecture</SectionHeadline>
                    <Para>
                        Supporting many equipment types meant components that could absorb changing
                        content without a new interface each time. This is where the work started
                        moving past traditional UX &mdash; a decision could not exist only as a
                        mockup. I had to answer: how should this component behave? What information
                        does it need? What happens when the content changes? Can the pattern carry
                        other equipment and workflows? How does it translate into production code?
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Working Across Global Teams</SectionHeadline>
                    <Para>
                        I led a five-person cross-functional team while coordinating with
                        international developers responsible for other parts of the platform. That
                        took more than handing off screens: requirements had to communicate component
                        behavior, application states, interaction logic, expected data, technical
                        constraints, and acceptance criteria.
                    </Para>
                    <Para>
                        Across time zones, ambiguity is expensive. The clearer the product and
                        implementation contracts, the less rework happened between teams &mdash;
                        which is where my thinking about engineering documentation and cross-team
                        interfaces started.
                    </Para>
                    <DiagramSlot>
                        <ReleaseLoop />
                    </DiagramSlot>
                    <Para>
                        I took part in customer interviews, enterprise user testing, and A/B testing
                        across iterative releases. The findings were not limited to visual changes
                        &mdash; they moved navigation, information architecture, application
                        behavior, and how features were implemented.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>What I Shipped</SectionHeadline>
                    <Shipped>
                        {SHIPPED.map((item) => (
                            <ShippedItem key={item.name} color={item.color}>
                                <ShippedName color={item.color}>{item.name}</ShippedName>
                                <ShippedDesc>{item.desc}</ShippedDesc>
                            </ShippedItem>
                        ))}
                    </Shipped>
                    <DiagramSlot>
                        <RoleDomains />
                    </DiagramSlot>
                    <MetricsCard
                        metrics={[
                            {
                                value: "5-Person Team",
                                label: "Led",
                                description: "Cross-functional product and development team",
                                color: colors.emerald[500],
                            },
                            {
                                value: "Global",
                                label: "Deployment",
                                description: "Supporting Caterpillar customers across international markets",
                                color: colors.cyan[300],
                            },
                            {
                                value: "Print → Digital",
                                label: "Transformation",
                                description: "Maintenance documentation became interactive workflows",
                                color: colors.amber[400],
                            },
                        ]}
                    />
                </Section>

                <Section>
                    <SectionHeadline>Retrospective</SectionHeadline>
                    <Para>
                        Caterpillar is where the line between designing software and engineering
                        software started disappearing for me. I began with a problem that sounded
                        like UX &mdash; how do we make these manuals easier to use? &mdash; and
                        solving it required answering engineering questions instead.
                    </Para>
                    <Para>
                        How should the information be structured? What becomes a reusable component?
                        How do different machines share the same patterns? How do maintenance, parts,
                        dealers, and service connect? How do we communicate those contracts across
                        distributed teams?
                    </Para>
                    <Pull>
                        I wasn&rsquo;t only interested in what the interface should look like. I
                        wanted to understand how the product should work &mdash; and then build it.
                    </Pull>
                </Section>
            </Body>
        </Layout>

        <NDADisclosure />
    </PageWrapper>
);

export default Caterpillar;
